import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Card, CardContent, Grid, Alert, Snackbar,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Switch
} from '@mui/material';
import { Refresh, Add } from '@mui/icons-material';
import { adminAPI } from '../../services/api';

/**
 * 🎟️ Вкладка «Лунный марафон» (акция Lunar Start) — перенесена из Promo.js
 * как есть. Создание кодов для блогеров: код, бонус USDT, срок, лимит.
 */
function LunarStartTab() {
  const [codes, setCodes] = useState([]);
  const [activations, setActivations] = useState([]);
  const [stats, setStats] = useState({ activated: 0, opened: 0, extended: 0, notExtended: 0 });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [form, setForm] = useState({
    code: '',
    bonusUsdt: 50,
    validDays: 14,
    maxActivations: 1000
  });
  const [creating, setCreating] = useState(false);

  const notify = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const loadCodes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getPromoCodes();
      setCodes(res.data.data || []);
    } catch (e) {
      notify(e.response?.data?.error || 'Ошибка загрузки промокодов', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadActivations = useCallback(async () => {
    try {
      const res = await adminAPI.getPromoActivations();
      setActivations(res.data.data.list || []);
      setStats(res.data.data.stats || { activated: 0, opened: 0, extended: 0, notExtended: 0 });
    } catch (e) {
      notify(e.response?.data?.error || 'Ошибка загрузки активаций', 'error');
    }
  }, []);

  useEffect(() => { loadCodes(); loadActivations(); }, [loadCodes, loadActivations]);

  const handleCreate = async () => {
    if (!form.code.trim()) {
      notify('Укажите код', 'error');
      return;
    }
    setCreating(true);
    try {
      await adminAPI.createPromoCode({
        code: form.code.trim(),
        bonusUsdt: Number(form.bonusUsdt) || 50,
        validDays: Number(form.validDays) || 14,
        maxActivations: Number(form.maxActivations) || 1000
      });
      notify(`Промокод «${form.code.trim().toUpperCase()}» создан`);
      setForm({ code: '', bonusUsdt: 50, validDays: 14, maxActivations: 1000 });
      loadCodes();
    } catch (e) {
      notify(e.response?.data?.error || 'Ошибка создания', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (codeRow) => {
    try {
      await adminAPI.togglePromoCode(codeRow.id, !codeRow.is_active);
      setCodes((prev) => prev.map((c) =>
        c.id === codeRow.id ? { ...c, is_active: !c.is_active } : c
      ));
    } catch (e) {
      notify(e.response?.data?.error || 'Ошибка переключения', 'error');
    }
  };

  const statusChip = (c) => {
    if (!c.is_active) return <Chip size="small" label="Выключен" color="default" />;
    if (c.not_started) {
      const d = new Date(c.valid_from);
      const p2 = (n) => String(n).padStart(2, '0');
      return <Chip size="small" label={`с ${p2(d.getDate())}.${p2(d.getMonth() + 1)} ${p2(d.getHours())}:${p2(d.getMinutes())}`} color="info" />;
    }
    if (c.is_expired) return <Chip size="small" label="Истёк" color="warning" />;
    if (c.activations_used >= c.max_activations) return <Chip size="small" label="Лимит" color="error" />;
    return <Chip size="small" label="Активен" color="success" />;
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        Промокод даёт юзеру бонус USDT на открытие Лунного Марафона.
        Бонус не выводится; прибыль 1-го периода — только в зачёт.
        Один акционный бонус на юзера.
      </Alert>

      {/* Создание промокода */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Add fontSize="small" /> Новый промокод
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth label="Код" value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="BLOGGER2026"
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth type="number" label="Бонус, USDT" value={form.bonusUsdt}
                onChange={(e) => setForm({ ...form, bonusUsdt: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth type="number" label="Срок, дней" value={form.validDays}
                onChange={(e) => setForm({ ...form, validDays: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth type="number" label="Лимит активаций" value={form.maxActivations}
                onChange={(e) => setForm({ ...form, maxActivations: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Button
                fullWidth variant="contained" onClick={handleCreate} disabled={creating}
                startIcon={creating ? <CircularProgress size={18} /> : <Add />}
              >
                Создать
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Список промокодов */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="h6">Все промокоды</Typography>
          <Button startIcon={<Refresh />} onClick={loadCodes} disabled={loading}>Обновить</Button>
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
        ) : codes.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            Промокодов пока нет
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ '& th, & td': { px: 0.5, py: 0.6, fontSize: '0.72rem', whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Код</TableCell>
                  <TableCell align="right">Бонус</TableCell>
                  <TableCell align="center">Актив.</TableCell>
                  <TableCell>До</TableCell>
                  <TableCell align="center">Статус</TableCell>
                  <TableCell align="center">Вкл</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {codes.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell><strong>{c.code}</strong></TableCell>
                    <TableCell align="right">{Number(c.bonus_usdt).toFixed(0)}$</TableCell>
                    <TableCell align="center">{c.activations_used}/{c.max_activations}</TableCell>
                    <TableCell>{new Date(c.valid_until).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' })}</TableCell>
                    <TableCell align="center">{statusChip(c)}</TableCell>
                    <TableCell align="center">
                      <Switch checked={!!c.is_active} onChange={() => handleToggle(c)} size="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Метрики акции */}
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={6} sm={3}>
          <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4">{stats.activated}</Typography>
            <Typography variant="caption" color="text.secondary">Активировали</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4">{stats.opened}</Typography>
            <Typography variant="caption" color="text.secondary">Открыли марафон</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" sx={{ color: '#26a17b' }}>{stats.extended}</Typography>
            <Typography variant="caption" color="text.secondary">Продлили (2-й период)</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" sx={{ color: '#ff9800' }}>{stats.notExtended}</Typography>
            <Typography variant="caption" color="text.secondary">Не продлили</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* Список активировавших */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="h6">Кто активировал ({activations.length})</Typography>
          <Button startIcon={<Refresh />} onClick={loadActivations}>Обновить</Button>
        </Box>
        {activations.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            Активаций пока нет
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ '& th, & td': { px: 0.5, py: 0.6, fontSize: '0.72rem', whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Юзернейм</TableCell>
                  <TableCell align="right">Бонус</TableCell>
                  <TableCell>Когда</TableCell>
                  <TableCell align="center">Статус</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activations.map((a, i) => (
                  <TableRow key={i}>
                    <TableCell>{a.username ? '@' + a.username : (a.first_name || `ID ${a.user_id}`)}</TableCell>
                    <TableCell align="right">{Number(a.bonus_usdt).toFixed(0)}$</TableCell>
                    <TableCell>{new Date(a.activated_at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' })}</TableCell>
                    <TableCell align="center">
                      {a.mstatus === 'overdue'
                        ? <Chip size="small" label={`не продлил ${a.period}`} color="warning" sx={{ height: 20, fontSize: '0.68rem' }} />
                        : a.mstatus === 'active'
                          ? <Chip size="small" label={`идёт период ${a.period}`} color={a.period >= 2 ? 'success' : 'info'} sx={{ height: 20, fontSize: '0.68rem' }} />
                          : <Chip size="small" label="Активировал" color="default" sx={{ height: 20, fontSize: '0.68rem' }} />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LunarStartTab;
